import { Table, Button, Tag, Space, Popconfirm, message, Select, notification } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import moment from "moment";
import React, { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "react-query";
import {
  useGetAllPermissions,
  useUpdatePermissionStatus,
} from "../../../../../services/permissions.service";
import { useStaffGroup } from "./StaffGroupContext";

const { RangePicker } = DatePicker;
const { Option } = Select;

const Permissions = () => {
  const group = useStaffGroup();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [api, contextHolder] = notification.useNotification();

  const params = useMemo(() => {
    const p = {
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "permission",
      group,
    };
    if (statusFilter) {
      p.status = statusFilter;
    }

    console.log("Permissions.js - Generated params:", p);

    return p;
  }, [dateRange, statusFilter, group]);

  const { data: permissionsData, isLoading } = useGetAllPermissions({}, params);

  const data = useMemo(() => {
    if (!permissionsData) return [];
    return permissionsData.filter((item) => item.type === "permission") || [];
  }, [permissionsData]);

  const updatePermission = useUpdatePermissionStatus({
    onSuccess: (response, variables) => {
      const { requestId, data: updateData } = variables;
      const permission = data.find((item) => item._id === requestId);

      if (permission) {
        const statusText =
          updateData.status === "approved" ? "Approved" : "Rejected";

        message.success(`Permission ${statusText.toLowerCase()} successfully`);
      }

      queryClient.invalidateQueries("allPermissions");
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.detail || "Failed to update permission"
      );
    },
  });

  const handleApprove = useCallback(
    (id, record) => {
      updatePermission.mutate({
        requestId: id,
        data: { status: "approved" },
      });
    },
    [updatePermission]
  );

  const handleDecline = useCallback(
    (id, record) => {
      updatePermission.mutate({
        requestId: id,
        data: { status: "rejected" },
      });
    },
    [updatePermission]
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "green";
      case "pending":
        return "orange";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const getPermissionTypeColor = (type) => {
    switch (type) {
      case "check_in":
        return "blue";
      case "check_out":
        return "purple";
      case "flex":
        return "cyan";
      default:
        return "default";
    }
  };

  const getPermissionTypeText = (type) => {
    switch (type) {
      case "check_in":
        return "Late Check-in";
      case "check_out":
        return "Early Check-out";
      case "flex":
        return "Flex";
      default:
        return type || "Permission";
    }
  };

  const [dates, setDates] = useState(null);

  const disabledDate = (current) => {
    if (!dates || dates.length === 0) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], "days") > 13;
    const tooEarly = dates[0] && dates[0].diff(current, "days") > 13;
    return !!tooEarly || !!tooLate;
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([]);
    }
  };

  const handleDateChange = (val) => {
    console.log("Date range changed to:", val);
    setDateRange(val);
  };

  const handleStatusFilterChange = (value) => {
    console.log("Status filter changed to:", value);
    setStatusFilter(value);
  };

  const columns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "from_time",
        key: "date",
        align: "center",
        render: (text, record) => (
          <div className="font-semibold">
            {text
              ? moment(text).format("DD/MM/YYYY")
              : moment(record.created_at).format("DD/MM/YYYY")}
          </div>
        ),
      },
      {
        title: "Instructor",
        dataIndex: "instructor_name",
        key: "name",
        align: "center",
        render: (text) => <div className="capitalize">{text || "Unknown"}</div>,
      },
      {
        title: "Type",
        key: "permissionType",
        align: "center",
        render: (_, record) => (
          <Tag
            color={getPermissionTypeColor(record.permission_for)}
            className="capitalize"
          >
            {getPermissionTypeText(record.permission_for)}
          </Tag>
        ),
      },
      {
        title: "Permission Time",
        key: "permissionTime",
        align: "center",
        render: (_, record) => {
          if (record.permission_for === "check_in") {
            return (
              <div className="flex flex-col">
                <span className="text-green-600 font-medium">
                  From:{" "}
                  {record.from_time
                    ? moment(record.from_time).format("hh:mm A")
                    : "-"}
                </span>
                <span className="text-green-600 font-medium">
                  To:{" "}
                  {record.to_time
                    ? moment(record.to_time).format("hh:mm A")
                    : "-"}
                </span>
              </div>
            );
          } else if (record.permission_for === "check_out") {
            return (
              <div className="flex flex-col">
                <span className="text-red-600 font-medium">
                  From:{" "}
                  {record.from_time
                    ? moment(record.from_time).format("hh:mm A")
                    : "-"}
                </span>
                <span className="text-red-600 font-medium">
                  To:{" "}
                  {record.to_time
                    ? moment(record.to_time).format("hh:mm A")
                    : "-"}
                </span>
              </div>
            );
          } else if (record.permission_for === "flex") {
            return (
              <div className="flex flex-col">
                <span className="text-blue-600 font-medium">
                  From:{" "}
                  {record.from_time
                    ? moment(record.from_time).format("hh:mm A")
                    : "-"}
                </span>
                <span className="text-blue-600 font-medium">
                  To:{" "}
                  {record.to_time
                    ? moment(record.to_time).format("hh:mm A")
                    : "-"}
                </span>
              </div>
            );
          }
          return "-";
        },
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        align: "center",
        render: (text) => <span className="text-gray-600">{text || "-"}</span>,
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        render: (_, record) => (
          <Tag
            color={getStatusColor(record.status)}
            className="capitalize px-3 py-1"
          >
            {record.status === "approved"
              ? "Approved"
              : record.status === "rejected"
              ? "Rejected"
              : "Pending"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        render: (_, record) => {
          if (record.status === "approved" || record.status === "rejected") {
            return (
              <Tag
                color={getStatusColor(record.status)}
                className="capitalize px-3 py-1"
              >
                {record.status === "approved" ? "Approved" : "Rejected"}
              </Tag>
            );
          }

          return (
            <Space size="middle">
              <Popconfirm
                title="Approve Permission Request"
                description={`Approve ${
                  record.instructor_name || "instructor"
                }'s permission request? This will send them a push notification.`}
                onConfirm={() => handleApprove(record._id, record)}
                okText="Yes, Approve"
                cancelText="Cancel"
                okButtonProps={{ type: "primary" }}
              >
                <Button type="primary" size="small">
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Reject Permission Request"
                description={`Reject ${
                  record.instructor_name || "instructor"
                }'s permission request? This will send them a push notification.`}
                onConfirm={() => handleDecline(record._id, record)}
                okText="Yes, Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" danger size="small">
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [handleApprove, handleDecline]
  );

  return (
    <div className="mt-8">
      {contextHolder}
      <div className="bg-white drop-shadow-md p-8 rounded-lg">
        <div className="text-2xl text-orange-500 font-semibold py-4">
         Admin - Permissions Management
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold">Select Date Range:</span>
            <RangePicker
              format="DD/MM/YYYY"
              value={dateRange}
              dates={dates}
              onCalendarChange={(val) => setDates(val)}
              onChange={handleDateChange}
              onOpenChange={onOpenChange}
              disabledDate={disabledDate}
              allowClear={false}
              style={{ width: 300 }}
            />
            <span className="text-base font-semibold">Filter by Status:</span>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: 150 }}
              placeholder="All Status"
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </div>
        </div>
        <Table
          className="rounded-2xl pt-6"
          columns={columns}
          dataSource={data?.length > 0 ? data : []}
          loading={isLoading}
          rowKey="_id"
        />
      </div>
    </div>
  );
};

export default Permissions;
